/* src/components/replay/hooks/useReplaySharing.js */
import { useState, useCallback, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Initialize Firebase Functions outside the hook
let makeReplayPublicCallable;
try {
    const functionsInstance = getFunctions();
    makeReplayPublicCallable = httpsCallable(functionsInstance, 'makeReplayPublic');
} catch (e) {
    console.error("ReplaySharing Hook: Firebase Functions could not be initialized:", e);
    makeReplayPublicCallable = null;
}

/**
 * Hook to manage the state and logic for sharing a replay.
 * @param {string | null} sessionId - The ID of the session being replayed.
 * @param {string | null} profileId - The ID of the profile owning the session.
 * @param {boolean} initialIsPublic - Whether the replay is initially public.
 * @param {boolean} isPublicView - True if viewing a public share link (disables sharing actions).
 * @param {boolean} isLocalReplay - True if viewing a locally generated replay (disables sharing).
 */
export const useReplaySharing = (sessionId, profileId, initialIsPublic, isPublicView, isLocalReplay) => {
    const [isSharing, setIsSharing] = useState(false);
    const [shareError, setShareError] = useState(null);
    const [showShareLinkModal, setShowShareLinkModal] = useState(false);
    const [shareLink, setShareLink] = useState('');
    const [isNowPublic, setIsNowPublic] = useState(initialIsPublic);

    // Sync public status if the initial prop changes after mount
    useEffect(() => {
        setIsNowPublic(initialIsPublic);
    }, [initialIsPublic]);

    // Determine if sharing actions are allowed
    const canShare = !isPublicView && !isLocalReplay && !!sessionId && !!profileId && !!makeReplayPublicCallable;

    // Generate link function (used internally and by copy)
    const generateLink = useCallback(() => {
        if (sessionId) {
             return `${window.location.origin}/share/replay/${sessionId}`;
        }
        return '';
    }, [sessionId]);

    // Copy link handler
    const handleCopyLink = useCallback(() => {
        let linkToCopy = shareLink || generateLink(); // Use existing or generate new
        if (linkToCopy) {
            navigator.clipboard.writeText(linkToCopy).then(() => {
                setShowShareLinkModal(false); // Close modal on successful copy
                alert("Link copied to clipboard!");
            }).catch(err => {
                // If copy fails, ensure the modal stays open and show the link
                setShareLink(linkToCopy); // Ensure link state is set
                setShowShareLinkModal(true);
                alert('Auto-copy failed. Please copy the link manually.');
                console.error('Clipboard copy error:', err);
            });
        } else {
            alert("Could not generate or copy the link.");
            setShareError("Failed to generate the share link.");
        }
    }, [shareLink, generateLink]); // Dependencies

    // Main share/copy action handler
    const handleShareOrCopy = useCallback(async () => {
        // If already public, just copy the link
        if (isNowPublic) {
            handleCopyLink();
            return;
        }

        // If cannot share or already sharing, do nothing (or show message)
        if (!canShare || isSharing) {
            alert("Cannot share this replay at the moment."); // Or set an error message
            return;
        }

        setIsSharing(true);
        setShareError(null);

        try {
            // Call the Firebase Function
            const result = await makeReplayPublicCallable({ profileId: profileId, sessionId: sessionId });

            if (result?.data?.success) {
                setIsNowPublic(true); // Update state to public
                const link = generateLink();
                setShareLink(link); // Store the generated link
                setShowShareLinkModal(true); // Show the modal with the link
            } else {
                // Throw error if backend indicates failure
                throw new Error(result?.data?.message || 'Backend failed to make replay public.');
            }
        } catch (error) {
            const errMsg = error.message || 'An unknown error occurred during sharing.';
            setShareError(errMsg);
            alert(`Sharing failed: ${errMsg}`); // Show alert
            console.error('Share error:', error);
            setShowShareLinkModal(false); // Ensure modal is closed on error
        } finally {
            setIsSharing(false); // Reset loading state
        }
    }, [profileId, sessionId, isSharing, isNowPublic, canShare, handleCopyLink, generateLink]); // Dependencies

    const closeShareModal = useCallback(() => {
        setShowShareLinkModal(false);
    }, []);

    return {
        isSharing,
        shareError,
        showShareLinkModal,
        shareLink,
        isNowPublic,
        canShare,
        handleShareOrCopy,
        handleCopyLink,
        closeShareModal,
        // Expose generateLink if needed externally, otherwise keep internal
        // generateLink
    };
};